import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useState } from "react";
import { useGetList, useGetOne } from "react-admin";
import type { DrivingSchoolAccounts } from "../details/student/AccountBalance";
import type { ManualRefund, MonetaryFlowEnum } from "../generated/backendClient";
import type { DrivingSchool } from "../providers/drivingSchoolsProvider";
import type { FundingInstructions } from "../providers/backendUserProvider";
import type { CreditNote } from "../providers/creditNotesProvider";
import type { Invoice } from "../providers/invoicesProvider";
import type { Student } from "../providers/studentsProvider";
import { buildStudentCostOverview } from "../pdf/studentCostOverview/buildStudentCostOverview";
import { canShowBankTransferInstructions } from "../pdf/studentCostOverview/canShowBankTransferInstructions";
import {
  StudentCostOverviewPdfExportError,
  runStudentCostOverviewPdfExportPhase,
  type StudentCostOverviewPdfExportDiagnostics,
  type StudentCostOverviewPdfExportType,
} from "../pdf/studentCostOverview/studentCostOverviewPdfExportDiagnostics";
import { LocalizedError } from "../utils/LocalizedError";

export function CreateStudentCostOverviewPdfMenu(props: {
  student: Student;
  accounts?: DrivingSchoolAccounts;
  monetaryFlow?: MonetaryFlowEnum;
}) {
  const { student, accounts, monetaryFlow } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<"totalCostOverview" | "balanceList">();
  const {
    data: drivingSchool,
    error: drivingSchoolError,
    isLoading: drivingSchoolIsLoading,
  } = useGetOne<DrivingSchool>("drivingSchools", {
    id: student.drivingSchoolId,
  });
  const {
    data: invoices,
    error: invoicesError,
    isLoading: invoicesAreLoading,
  } = useGetList<Invoice>("b2cInvoices", {
    filter: { studentId: student.id },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "createdAt", order: "ASC" },
  });
  const {
    data: creditNotes,
    error: creditNotesError,
    isLoading: creditNotesAreLoading,
  } = useGetList<CreditNote>("b2cCreditNotes", {
    filter: { recipientId: student.autovioUserId },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "createdAt", order: "ASC" },
  });
  const {
    data: manualRefunds,
    error: manualRefundsError,
    isLoading: manualRefundsAreLoading,
  } = useGetList<ManualRefund>("manualRefunds", {
    filter: { drivingSchoolId: student.drivingSchoolId },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "created_at", order: "ASC" },
  });
  const shouldLoadFundingInstructions = canShowBankTransferInstructions(student);
  const {
    data: fundingInstructions,
    error: fundingInstructionsError,
    isLoading: fundingInstructionsAreLoading,
  } = useGetOne<FundingInstructions>(
    "fundingInstructions",
    { id: student.id },
    { enabled: shouldLoadFundingInstructions },
  );

  const menuOpen = !!anchorEl;
  const dataIsLoading =
    drivingSchoolIsLoading ||
    invoicesAreLoading ||
    creditNotesAreLoading ||
    manualRefundsAreLoading ||
    (shouldLoadFundingInstructions && fundingInstructionsAreLoading);
  const dataLoadError = firstPdfDataLoadError([
    { error: drivingSchoolError, label: "Fahrschule" },
    { error: invoicesError, label: "Rechnungen" },
    { error: creditNotesError, label: "Gutschriften" },
    { error: manualRefundsError, label: "Rückerstattungen" },
    ...(shouldLoadFundingInstructions ? [{ error: fundingInstructionsError, label: "Überweisungsdaten" }] : []),
  ]);

  if (dataIsLoading) {
    return null;
  }

  function buildOverview(diagnostics: StudentCostOverviewPdfExportDiagnostics) {
    if (!drivingSchool || !invoices || !creditNotes || !manualRefunds || dataIsLoading) {
      throw new LocalizedError("Die Daten für den PDF-Export sind noch nicht vollständig geladen.");
    }
    return runStudentCostOverviewPdfExportPhase("buildOverview", diagnostics, () =>
      buildStudentCostOverview({
        student,
        drivingSchool,
        invoices,
        creditNotes,
        manualRefunds: manualRefunds.filter((manualRefund) => manualRefundBelongsToStudent(manualRefund, student)),
        accounts,
        monetaryFlow,
        fundingInstructions,
      }),
    );
  }

  async function createTotalCostOverviewPdf() {
    setGeneratingPdf("totalCostOverview");
    await runSafely(
      async () => {
        const fileName = fileNameForStudent(student, "Kostenuebersicht");
        const diagnostics = pdfExportDiagnostics("totalCostOverview", fileName);
        assertPdfDataLoaded(diagnostics, dataLoadError);
        const { downloadStudentTotalCostOverviewPdf } =
          await import("../pdf/studentCostOverview/downloadStudentTotalCostOverviewPdf");
        await downloadStudentTotalCostOverviewPdf({
          overview: await buildOverview(diagnostics),
          fileName,
          diagnostics,
        });
      },
      {
        successMessage: "PDF-Kostenübersicht wurde erstellt.",
        errorMessage: pdfExportErrorMessage("Fehler beim Erstellen der PDF-Kostenübersicht.", dataLoadError),
      },
    );
    setGeneratingPdf(undefined);
    setAnchorEl(null);
  }

  async function createBalanceListPdf() {
    setGeneratingPdf("balanceList");
    await runSafely(
      async () => {
        const fileName = fileNameForStudent(student, "Saldenliste");
        const diagnostics = pdfExportDiagnostics("balanceList", fileName);
        assertPdfDataLoaded(diagnostics, dataLoadError);
        const { downloadStudentBalanceListPdf } =
          await import("../pdf/studentCostOverview/downloadStudentBalanceListPdf");
        await downloadStudentBalanceListPdf({
          overview: await buildOverview(diagnostics),
          fileName,
          diagnostics,
        });
      },
      {
        successMessage: "PDF-Saldenliste wurde erstellt.",
        errorMessage: pdfExportErrorMessage("Fehler beim Erstellen der PDF-Saldenliste.", dataLoadError),
      },
    );
    setGeneratingPdf(undefined);
    setAnchorEl(null);
  }

  return (
    <>
      <Tooltip title="Weitere Aktionen">
        <span>
          <IconButton
            aria-label="Weitere Rechnungsaktionen"
            aria-controls={menuOpen ? "student-invoice-actions-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            disabled={!!generatingPdf}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu id="student-invoice-actions-menu" anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
        <MenuItem disabled={!!generatingPdf} onClick={createTotalCostOverviewPdf}>
          {generatingPdf === "totalCostOverview"
            ? "PDF-Kostenübersicht wird erstellt ..."
            : "PDF-Kostenübersicht erstellen"}
        </MenuItem>
        <MenuItem disabled={!!generatingPdf} onClick={createBalanceListPdf}>
          {generatingPdf === "balanceList" ? "PDF-Saldenliste wird erstellt ..." : "PDF-Saldenliste erstellen"}
        </MenuItem>
      </Menu>
    </>
  );

  function pdfExportDiagnostics(
    exportType: StudentCostOverviewPdfExportType,
    fileName: string,
  ): StudentCostOverviewPdfExportDiagnostics {
    return {
      exportType,
      fileName,
      studentId: student.id,
      autovioUserId: student.autovioUserId,
      drivingSchoolId: student.drivingSchoolId,
      invoiceCount: invoices?.length,
      creditNoteCount: creditNotes?.length,
      manualRefundCount: manualRefunds?.length,
      studentManualRefundCount: manualRefunds?.filter((manualRefund) =>
        manualRefundBelongsToStudent(manualRefund, student),
      ).length,
      hasFundingInstructions: !!fundingInstructions,
      hasLogoUrl: !!drivingSchool?.logoUrl,
    };
  }
}

function assertPdfDataLoaded(
  diagnostics: StudentCostOverviewPdfExportDiagnostics,
  dataLoadError: PdfDataLoadError | undefined,
) {
  if (!dataLoadError) {
    return;
  }
  throw new StudentCostOverviewPdfExportError("loadData", diagnostics, dataLoadError.error);
}

function pdfExportErrorMessage(defaultMessage: string, dataLoadError: PdfDataLoadError | undefined): string {
  if (!dataLoadError) {
    return defaultMessage;
  }
  return `${defaultMessage.replace(/[.!]$/, "")}: Die Daten für den PDF-Export konnten nicht geladen werden: ${dataLoadError.label}.`;
}

interface PdfDataLoadError {
  label: string;
  error: unknown;
}

function firstPdfDataLoadError(errors: Array<{ label: string; error: unknown }>): PdfDataLoadError | undefined {
  return errors.find((error) => !!error.error);
}

function fileNameForStudent(student: Student, prefix: string): string {
  const studentName = transliterateGerman([student.lastName, student.firstName].filter(Boolean).join("-"))
    .toLowerCase()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${prefix}-${studentName || student.id}.pdf`;
}

function transliterateGerman(value: string): string {
  return value
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/Ä/g, "Ae")
    .replace(/Ö/g, "Oe")
    .replace(/Ü/g, "Ue");
}

function manualRefundBelongsToStudent(manualRefund: ManualRefund, student: Student): boolean {
  return manualRefund.user_firebase_uid === student.autovioUserId;
}
